<template>
  <BareFullScreenPopup
    ref="popup"
    :closeable="true"
    :title="getTitle"
    @visibility-change="onVisibilityChange"
  >
    <div
      v-if="isPaymentLinkLoading && !getInvoiceLink"
      class="payment-link-popup__loader"
    >
      <SpinnerLoader :scale="2" />
    </div>

    <div v-else-if="getInvoiceLink && !isCopySuccess" class="payment-link-popup">
      <span class="payment-link-popup__heading">Рахунок сформовано</span>
      <span class="payment-link-popup__label">До сплати:</span>
      <span class="payment-link-popup__value">{{ paySum }} грн</span>

      <div class="payment-link-popup__qr">
        <img v-if="getQrCode" :src="getQrCode" alt="QR-код">
        <span class="payment-link-popup__qr-label">QR-код переходу на сторінку оплати</span>
      </div>

      <CopyInput
        :value="getInvoiceLink"
        class="payment-link-popup__link-input"
      />

      <div class="payment-link-popup__commission">
        <template v-if="getAcquiringPaidBy === 'uinsure'">Комісія не стягується</template>
        <template v-else>
          + {{ getPercentageAcquiring }}% за послуги {{ getPaymentSystemTitle }}
          <span v-if="getAcquiringPaidBy === 'agent'"> сплачуються з комісійної винагороди агента</span>
        </template>
      </div>
      <div class="payment-link-popup__notice">рахунок діє до 00:00 наступного дня</div>

      <BlueButton
        class="payment-link-popup__pay-btn"
        @click="goToPayment"
      >
        Перейти до сплати
      </BlueButton>
      <BlueOutlineButton
        class="payment-link-popup__cancel-btn"
        @click="close"
      >
        Скасувати
      </BlueOutlineButton>
    </div>

    <div v-else-if="isCopySuccess" class="payment-link-popup">
      <span class="payment-link-popup__success-msg">Посилання на оплату успішно скопійоване!</span>
      <BlueOutlineButton
        class="payment-link-popup__action-btn"
        @click="close"
      >
        Закрити
      </BlueOutlineButton>
    </div>
  </BareFullScreenPopup>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { BlueButton, BlueOutlineButton, CopyInput } from '@/shared/ui/form';
import { SpinnerLoader } from '@/shared/ui/loader';
import { BareFullScreenPopup } from '@/shared/ui/popup';

export default {
  name: 'PaymentLinkPopup',
  components: { BareFullScreenPopup, BlueOutlineButton, BlueButton, CopyInput, SpinnerLoader },
  props: {
    orderId: {
      type: [String, Number],
      default: null,
    },
    price: {
      type: Number,
      default: 0,
    },
    providerData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      paySum: 0,
    };
  },
  computed: {
    ...mapGetters('paymentLinkModule', ['getInvoiceLink', 'getQrCode', 'isPaymentLinkLoading',
      'getPercentageAcquiring', 'getPaymentSystemTitle', 'getAcquiringPaidBy', 'isCopySuccess',
      'getPaymentLinkError']),
    getTitle() {
      if (this.isPaymentLinkLoading && !this.getInvoiceLink) {
        return 'Формуємо рахунок';
      }

      return null;
    },
  },
  methods: {
    ...mapActions('paymentLinkModule', ['createPaymentLink', 'setAcquiringInfo', 'setCopySuccess', 'clearInvoice']),
    open(orderId, price, providerData) {
      this.paySum = price;

      this.clearInvoice();
      this.setAcquiringInfo(providerData);

      this.$nextTick(() => {
        this.$refs.popup.open();
      });

      const providerChanged = this.providerData?.code !== providerData?.code
        || this.providerData?.paid_by !== providerData?.paid_by
        || (this.providerData?.term ?? null) !== (providerData?.term ?? null);
      const linkNotLoaded = !this.getInvoiceLink;

      if (linkNotLoaded || providerChanged) {
        this.triggerCreatePaymentLink(orderId || this.orderId, price, providerData);
      }
    },
    close() {
      this.$refs.popup?.close();
    },
    onVisibilityChange({ isVisible }) {
      if (!isVisible) {
        this.clearInvoice();
        this.$emit('closed');
      }
    },
    async triggerCreatePaymentLink(orderId, amount, providerData) {
      await this.createPaymentLink({
        orderId,
        amount,
        providerCode: providerData?.code,
        paidBy: providerData?.paid_by,
        invoiceType: providerData?.invoiceType ?? null,
        installmentTerm: providerData?.term ?? null,
      });

      if (this.getInvoiceLink) {
        this.$emit('invoice-created');
        return;
      }

      if (this.getPaymentLinkError) {
        this.$eventBus.$emit('payment-way-unavailable', {
          code: providerData?.code ?? null,
          paidBy: providerData?.paid_by ?? null,
          term: providerData?.term ?? null,
        });
        this.close();
      }
    },
    goToPayment() {
      if (!this.getInvoiceLink) {
        return;
      }
      window.location.href = this.getInvoiceLink;
    },
  },
};
</script>

<style scoped lang="scss">
.payment-link-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 0 16px;

  &__heading {
    font-size: 24px;
    font-weight: 500;
    line-height: 1;
    text-align: center;
    margin-bottom: 16px;
  }

  &__label {
    font-size: 20px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    margin-bottom: 4px;
  }

  &__value {
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
    text-align: center;
    margin-bottom: 16px;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
  }

  &__qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    img {
      max-width: 200px;
      max-height: 200px;
    }
  }

  &__qr-label {
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    color: #717680;
    text-align: center;
    margin-bottom: 24px;
  }

  &__link-input {
    width: 100%;
    max-width: 400px;
  }

  &__commission {
    font-size: 16px;
    font-weight: 400;
    color: #717680;
    text-align: center;
  }

  &__notice {
    font-size: 16px;
    font-weight: 400;
    color: #717680;
    text-align: center;
    margin-bottom: 24px;
  }

  &__pay-btn,
  &__action-btn,
  &__cancel-btn {
    width: 100%;
    max-width: 400px;
    padding: 16px 24px;
  }

  &__success-msg {
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    color: #24282C;
  }
}

::v-deep .fullscreen-popup__content-wrapper {
  max-width: 500px;
}
</style>
